import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ExportModal from '../common/ExportModal';
import UploadModal from '../common/UploadModal';
import EditableCell from '../common/EditableCell';
import FilterDropdown from '../common/FilterDropdown';
import { EXPORT_CONFIGS } from '../../config/exportConfigs';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import TransmittalAnalyticsDashboard from './TransmittalAnalyticsDashboard';

// Column definitions
const COLUMNS = [
  { key: 'uid', label: 'UID', type: 'text', width: 100 },
  { key: 'date', label: 'Date', type: 'date', width: 100 },
  { key: 'job_number', label: 'Job Number', type: 'text', width: 100 },
  { key: 'transmittal_number', label: 'Transmittal Number', type: 'text', width: 140 },
  { key: 'description', label: 'Description', type: 'text', width: 200 },
  { key: 'type', label: 'Type', type: 'text', width: 100 },
  { key: 'rev_number', label: 'Rev Number', type: 'text', width: 90 },
  { key: 'extra_number', label: 'Extra Number', type: 'text', width: 100 },
  { key: 'comments', label: 'Comments', type: 'text', width: 200 },
  { key: 'requested_by', label: 'Requested By', type: 'text', width: 120 },
  { key: 'from_party', label: 'From', type: 'text', width: 120 },
  { key: 'to_party', label: 'To', type: 'text', width: 120 },
  { key: 'iso_number', label: 'ISO Number', type: 'text', width: 100 },
  { key: 'drawing_number', label: 'Drawing Number', type: 'text', width: 120 },
  { key: 'status', label: 'Status', type: 'select', width: 100, options: ['Draft', 'Submitted', 'Approved', 'Rejected', 'Revised', 'Closed'] },
  { key: 'next_submission_date', label: 'Next Submission', type: 'date', width: 120 },
];

// Custom render for uid column (auto-generated, non-editable)
const renderTransmittalValue = (val, column) => {
  if (column.key === 'uid') {
    return <span style={{ color: '#6c757d' }}>{val || '(auto)'}</span>;
  }
  return null; // Use default formatting
};

const TransmittalLog = ({ projectId, onFilterStateChange }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const fetchItemsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchItemsRef.current) fetchItemsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('transmittalLog', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Use reusable hooks for filtering and sorting
  const filterHook = useTableFilter({ columns: COLUMNS, items });
  const sortHook = useTableSort({ columns: COLUMNS });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const downloadedJobsRef = useRef(new Set());


  const fetchItems = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/transmittal');
      if (response.data.success) setItems(response.data.data || []);
      else setError('Failed to load transmittal data');
    } catch (err) { console.error('Error:', err); setError('Failed to load data. Please try again.'); }
    finally { setLoading(false); }
  }, [instance, accounts]);

  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSaveCell = async (rowId, columnKey, newValue) => {
    try {
      setSavingRows(prev => new Set([...prev, rowId]));
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/transmittal/${rowId}`, { [columnKey]: newValue }, syncHeaders());
      if (response.data.success) { setItems(prev => prev.map(item => item.id === rowId ? { ...item, [columnKey]: newValue } : item)); showToast('Saved successfully'); }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Save failed: ${err.message}`, 'error'); }
    finally { setSavingRows(prev => { const next = new Set(prev); next.delete(rowId); return next; }); }
  };

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/transmittal', newRow, syncHeaders());
      if (response.data.success) { setItems(prev => [response.data.data, ...prev]); setNewRow({}); setIsAddingNew(false); showToast('New record added'); }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Add failed: ${err.message}`, 'error'); }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/transmittal/${rowId}`, null, syncHeaders());
      if (response.data.success) { setItems(prev => prev.filter(item => item.id !== rowId)); showToast('Deleted'); }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Delete failed: ${err.message}`, 'error'); }
  };

  const handleExport = async () => {
    if (exporting) return;
    try {
      setExporting(true); setShowExportModal(false);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/transmittal/export', null, syncHeaders());
      if (response.data.success) { setExportJobId(response.data.jobId); showToast(`Export started! Job ID: ${response.data.jobId}`, 'success'); }
      else throw new Error(response.data.message);
    } catch (error) { setExporting(false); showToast(`Export error: ${error.message}`, 'error'); }
  };

  const handleExportJobComplete = (jobData) => {
    showToast(`Export completed! ${jobData.processed_rows} records exported.`, 'success');
    setExporting(false); setExportJobId(null);
    if (jobData.job_id && !downloadedJobsRef.current.has(jobData.job_id)) {
      downloadedJobsRef.current.add(jobData.job_id);
      window.open(`/api/exports/download/${jobData.result?.filePath || jobData.job_id}`, '_blank');
    }
  };

  const handleExportJobError = (errorMessage) => { showToast(`Export failed: ${errorMessage}`, 'error'); setExporting(false); setExportJobId(null); };
  const handleUploadSuccess = (data) => { showToast(`Upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success'); fetchItems(); };
  const handleUploadError = (message) => { showToast(`Upload failed: ${message}`, 'error'); };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && filterHook.openFilterColumn) {
        filterHook.setOpenFilterColumn(null);
        filterHook.setFilterSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook]);

  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  // Report filter state to parent (for display in project header)
  useEffect(() => {
    if (onFilterStateChange) {
      onFilterStateChange({
        hasActiveFilters,
        searchQuery: searchTerm,
        activeFilters: filterHook.activeFilters.map(f => ({
          columnName: f.label,
          type: f.type,
          count: f.count,
          mode: f.mode,
          value: f.value
        })),
        onClearAll: handleClearAll
      });
    }
    // Cleanup on unmount
    return () => {
      if (onFilterStateChange) {
        onFilterStateChange(null);
      }
    };
  }, [hasActiveFilters, searchTerm, filterHook.activeFilters, handleClearAll, onFilterStateChange]);

  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Global search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item => COLUMNS.some(col => { 
        const val = item[col.key]; 
        return val !== null && val !== undefined && String(val).toLowerCase().includes(search); 
      }));
    }
    
    // Apply column filters
    result = filterHook.applyFilters(result);
    
    // Apply sorting
    result = sortHook.applySorting(result);
    
    return result;
  }, [items, searchTerm, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div style={{ display: 'inline-block', width: '48px', height: '48px', border: '4px solid #f3f3f3', borderTop: '4px solid #6c757d', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div><p style={{ marginTop: '1rem', color: '#666' }}>Loading Transmittal Log...</p><style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style></div>;
  if (error) return <div style={{ padding: '2rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', color: '#856404' }}><strong>⚠️ Error:</strong> {error}<button onClick={fetchItems} style={{ marginLeft: '1rem', padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retry</button></div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {toast.show && <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', color: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>{toast.message}</div>}

      {exportJobId && <JobStatusMonitor jobId={exportJobId} onComplete={handleExportJobComplete} onError={handleExportJobError} />}

      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="Search all columns..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '6px 12px', paddingLeft: '30px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px', width: '250px' }} />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>🔍</span>
          </div>
          <span style={{ fontSize: '14px', color: '#666' }}>Showing {filteredItems.length} of {items.length} records</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button onClick={() => setShowAnalytics(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📊 Analytics</button>
          <button onClick={() => setShowExportModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📤 Export</button>
          <button onClick={() => setShowUploadModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📥 Import</button>
          <button onClick={() => setIsAddingNew(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>➕ Add New</button>
        </div>
      </div>

      {isAddingNew && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #dee2e6' }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Add New Transmittal Record</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
            {COLUMNS.filter(col => col.key !== 'uid').map(col => (
              <div key={col.key}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: '#6c757d', marginBottom: '2px' }}>{col.label}</label>
                {col.type === 'select' ? (
                  <select value={newRow[col.key] || ''} onChange={(e) => setNewRow({ ...newRow, [col.key]: e.target.value })} style={{ width: '100%', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.85rem' }}>
                    <option value="">Select...</option>
                    {col.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : col.type === 'date' ? (
                  <input type="date" value={newRow[col.key] || ''} onChange={(e) => setNewRow({ ...newRow, [col.key]: e.target.value })} style={{ width: '100%', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.85rem' }} />
                ) : (
                  <input type="text" value={newRow[col.key] || ''} onChange={(e) => setNewRow({ ...newRow, [col.key]: e.target.value })} style={{ width: '100%', padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.85rem' }} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button onClick={handleAddNewRow} style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
            <button onClick={() => { setIsAddingNew(false); setNewRow({}); }} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', maxHeight: 'calc(100vh - 350px)', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '2200px' }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', color: '#495057', zIndex: 10, borderBottom: '1px solid #dee2e6' }}>
            <tr>
              <th style={{ padding: '12px 8px', textAlign: 'center', width: '60px', borderRight: '1px solid #dee2e6', position: 'sticky', left: 0, backgroundColor: '#f8f9fa', zIndex: 11 }}>Actions</th>
              {COLUMNS.map(col => (
                <th key={col.key} style={{ padding: '8px 4px', textAlign: 'left', width: col.width, borderRight: '1px solid #dee2e6', whiteSpace: 'nowrap', position: 'relative', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span onClick={() => sortHook.handleSort(col.key)} style={{ cursor: 'pointer', flex: 1 }} title="Click to sort">
                      {col.label}
                      {sortHook.sortColumn === col.key && <span style={{ marginLeft: '4px' }}>{sortHook.sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </span>
                    <span 
                      onClick={(e) => { e.stopPropagation(); filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); }} 
                      style={{ cursor: 'pointer', opacity: filterHook.hasColumnFilter(col.key) ? 1 : 0.5, fontSize: '0.75rem', color: filterHook.hasColumnFilter(col.key) ? '#10b981' : 'white' }} 
                      title="Filter"
                    >
                      🔽
                    </span>
                  </div>
                  {filterHook.openFilterColumn === col.key && (
                    <FilterDropdown
                      column={col}
                      uniqueValues={filterHook.getUniqueValues(col.key)}
                      selectedValues={filterHook.selectedValues[col.key] || []}
                      textFilter={filterHook.textFilters[col.key] || null}
                      numericFilter={filterHook.numericFilters[col.key] || null}
                      dateFilter={filterHook.dateFilters[col.key] || null}
                      filterSearch={filterHook.filterSearch}
                      onToggleValue={filterHook.toggleValue}
                      onSelectAll={filterHook.selectAll}
                      onClearColumn={filterHook.clearColumn}
                      onApplyTextFilter={filterHook.applyTextFilter}
                      onApplyNumericFilter={filterHook.applyNumericFilter}
                      onApplyDateFilter={filterHook.applyDateFilter}
                      onClose={() => {
                        filterHook.setOpenFilterColumn(null);
                        filterHook.setFilterSearch('');
                      }}
                      onSearchChange={filterHook.setFilterSearch}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? <tr><td colSpan={COLUMNS.length + 1} style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>{searchTerm ? 'No matching records' : 'No records. Click "Add New" to create one.'}</td></tr> :
              filteredItems.map((item, index) => (
                <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '4px 8px', textAlign: 'center', borderRight: '1px solid #eee', position: 'sticky', left: 0, backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa', zIndex: 5 }}><button onClick={() => handleDeleteRow(item.id)} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }} title="Delete">🗑️</button></td>
                  {COLUMNS.map(col => <td key={col.key} style={{ padding: '4px 8px', borderRight: '1px solid #eee', backgroundColor: savingRows.has(item.id) ? '#fff3cd' : (col.key === 'uid' ? '#f8f9fa' : 'inherit') }}><EditableCell value={item[col.key]} row={item} column={col} onSave={handleSaveCell} disabled={col.key === 'uid'} renderValue={renderTransmittalValue} accentColor="#6c757d" /></td>)}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <ExportModal {...EXPORT_CONFIGS.transmittal} isOpen={showExportModal} onClose={() => setShowExportModal(false)} onExport={handleExport} isExporting={exporting} />
      <UploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onUploadSuccess={handleUploadSuccess} onUploadError={handleUploadError} config={UPLOAD_CONFIGS.TRANSMITTAL} />
      
      {/* Analytics Dashboard */}
      {showAnalytics && (
        <TransmittalAnalyticsDashboard
          data={items}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default TransmittalLog;

